import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useEsimAccess } from '~/composables/access'
import type { PackageList, EsimListProfile } from '~/composables/types/esimPackage'
import { useTracking } from '~/composables/useTracking'

type View = 'home' | 'packages' | 'register' | 'checkout' | 'profile'

const storeCache = new Map<string, ReturnType<typeof defineEsimStore>>()

const defineEsimStore = (instanceId: string) => defineStore(`esim-${instanceId}`, () => {
  const esimAccess = useEsimAccess()
  const { trackPurchase } = useTracking()

  const currentView = ref<View>('home')

  const countrySlug = ref<string>('')
  const countryName = ref<string>('')
  const currentInvoice = ref<string>('')
  const currentOrderNo = ref<string>('') 
  const registerEmail = ref<string>('')

  const packages = ref<PackageList[]>([])
  const profiles = ref<EsimListProfile[]>([])

  const activePackageTab = ref('standard')
  const activeDeviceTab = ref<'ios' | 'android'>('ios')

  const selectedPackage = ref<PackageList | null>(null)
  const selectedCount = ref(1)

  const loading = reactive({
    packages: false,
    order: false,
    profiles: false,
  })

  let packagesAbortController: AbortController | null = null

  // Getters
  const currentPackages = computed(() => {
    const standard = packages.value.filter(pkg => pkg.dataType === 1)
    const dailyLimit = packages.value.filter(pkg => pkg.dataType === 2)
    return activePackageTab.value === 'standard' ? standard : dailyLimit
  })

  const totalAmount = computed(() =>
    selectedPackage.value ? selectedPackage.value.price * selectedCount.value : 0
  )

  // Private helpers
  const _abortPackages = () => {
    packagesAbortController?.abort()
    packagesAbortController = null
  }

  const _resetPackagesState = () => {
    packages.value = []
    selectedPackage.value = null
    activePackageTab.value = 'standard'
  }

  // Navigation actions
  const HomeStep = () => {
    _abortPackages()
    _resetPackagesState()
    currentView.value = 'home'
    countrySlug.value = ''
    countryName.value = ''
  }

  const PackageStep = async (slug: string, name: string) => {
    _abortPackages()
    _resetPackagesState()

    countrySlug.value = slug
    countryName.value = name
    currentView.value = 'packages'

    packagesAbortController = new AbortController()
    await fetchPackages(slug, packagesAbortController.signal)
  }

  const RegisterStep = (pkg: PackageList, count = 1) => {
    selectedPackage.value = pkg
    selectedCount.value = count
    currentView.value = 'register'
  }

  const CheckoutStep = async (pkg: PackageList, count = 1, email: string) => {
    registerEmail.value = email
    selectedPackage.value = pkg
    selectedCount.value = count

    const invoiceId = await OrderPackages(pkg, count, email)
    if (invoiceId) {
      currentView.value = 'checkout'
    }
  }

  const ProfileStep = (orderNo: string) => {
    trackPurchase(orderNo)
    currentOrderNo.value = orderNo
    currentView.value = 'profile'
  }

  const clearPackages = () => {
    _abortPackages()
    _resetPackagesState()
  }

  // Data actions
  const fetchPackages = async (locationCode: string, signal?: AbortSignal) => {
    loading.packages = true
    try {
      const res = await esimAccess.getPackages({ locationCode }, signal)
      packages.value = res.data || []
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
      throw err
    } finally {
      if (!signal?.aborted) {
        loading.packages = false
      }
    }
  }

  const OrderPackages = async (pkg: PackageList, count: number, email: string) => {
    loading.order = true
    try {
      const orderInfos = [{ packageCode: pkg.packageCode, count, price: pkg.price }]
      const res = await esimAccess.createOrder({ packageInfoList: orderInfos, email })
      if (res.success && res.invoiceId) {
        currentInvoice.value = res.invoiceId
        return res.invoiceId
      }
      return null
    } finally {
      loading.order = false
    }
  }

  const queryPackages = async () => {
    loading.profiles = true
    try {
      const res = await esimAccess.queryProfiles({
        orderNo: currentOrderNo.value,
        pager: { pageNum: 1, pageSize: 10 },
      })
      if (!res.success || !res.data?.obj.esimList) return
      profiles.value = res.data.obj.esimList
    } finally {
      loading.profiles = false
    }
  }

  const fetchDataInvoice = async (invoiceId: string) => {
    try {
      const invoice = await esimAccess.getInvoice(invoiceId)

      if (!invoice || !invoice._id) return false
      if (invoice.status !== 'pending') return false

      const pkg = invoice.packageInfoList?.[0]
      if (!pkg) return false

      currentInvoice.value = invoice._id
      registerEmail.value = invoice.email
      selectedCount.value = pkg.count ?? 1

      selectedPackage.value = {
        packageCode: pkg.packageCode ?? '',
        name: pkg.name ?? '',
        description: pkg.description ?? '',
        location: pkg.location ?? '',
        locationCode: pkg.locationCode ?? '',
        volume: pkg.volume ?? 0,
        duration: pkg.duration ?? 0,
        dataType: Number(pkg.dataType ?? 1) as 1 | 2,
        speed: pkg.speed ?? '',
        price: pkg.count > 1 ? pkg.price / pkg.count : pkg.price ?? 0,
      } as PackageList

      countryName.value = pkg.location ?? ''
      countrySlug.value = pkg.locationCode?.toLowerCase() ?? ''
      currentView.value = 'checkout'

      return true
    } catch (err) {
      console.error('fetchDataInvoice error:', err)
      return false
    }
  } 

  return {
    // State
    currentView,
    countrySlug,
    countryName,
    packages,
    profiles,
    loading,
    activePackageTab,
    activeDeviceTab,
    selectedPackage,
    selectedCount,
    currentInvoice,
    currentOrderNo,
    registerEmail,
    // Getters
    currentPackages,
    totalAmount,
    // Actions
    HomeStep,
    PackageStep,
    RegisterStep,
    CheckoutStep,
    ProfileStep,
    clearPackages,
    fetchPackages,
    OrderPackages,
    queryPackages,
    fetchDataInvoice,
  }
})

export const createEsimStore = (instanceId: string) => {
  if (!storeCache.has(instanceId)) {
    storeCache.set(instanceId, defineEsimStore(instanceId))
  }
  return storeCache.get(instanceId)!
}

export const useEsimStore = (instanceId: string) => createEsimStore(instanceId)()